/**
 * The `Albert-Acting-As` request header: the teacher a masquerading admin is acting as, sent so
 * the API applies masquerade in one place instead of per query.
 *
 * The masquerade store decides the value and the Apollo client owns the cache it invalidates, but
 * `BUILD_HEADER`, the Apollo link chain and the store can't import each other without a cycle
 * (the same reason `resources/modelResolverRef` exists). Each registers its part here instead.
 * Until they do, nothing is sent and nothing is reset.
 */
export const ACTING_AS_HEADER = 'Albert-Acting-As';

/** The error code of the 403 the API returns when the header names someone the user may not act as. */
export const ACTING_AS_FORBIDDEN = 'acting_as_forbidden';

/** The feature flag that turns the header on for an admin. */
export const ACTING_AS_HEADER_FLAG = 'acting_as_header';

let actingAsSource: () => string | null = () => null;
let refusalHandler: (refusedId: string | null) => void = () => {};
let cacheReset: () => Promise<unknown> = () => Promise.resolve();

/** Registers where the header's value comes from: a teacher id, or `null` to send nothing. */
export function setActingAsSource(source: () => string | null): void {
  actingAsSource = source;
}

/** The teacher id to send in the header, or `null` when the request should not carry it. */
export function getActingAsId(): string | null {
  return actingAsSource();
}

/** Registers what happens when the API refuses the header. */
export function setActingAsRefusalHandler(handler: (refusedId: string | null) => void): void {
  refusalHandler = handler;
}

/**
 * Called when a request is refused with {@link ACTING_AS_FORBIDDEN}, with the teacher id that
 * request named, so a late refusal can't end a newer masquerade.
 */
export function handleActingAsRefused(refusedId: string | null): void {
  refusalHandler(refusedId);
}

/** Registers how cached results are dropped when the header's value changes. */
export function setActingAsCacheReset(reset: () => Promise<unknown>): void {
  cacheReset = reset;
}

/**
 * Drops Apollo's cached results so none fetched under one identity is served to another. The
 * legacy client's cache needs no reset: the API reads the header only on `/graphql` and `/json`,
 * and the legacy client calls neither.
 */
export function resetActingAsCache(): Promise<unknown> {
  return cacheReset();
}
